import { useMemo, useState } from 'react';
import {
  downloadCsvZip,
  downloadText,
  runDatabaseExport,
  type CallerIdentity,
  type DbTable,
  type ExportFormat,
} from '@/lib/dbAdmin';

interface FormatOption {
  key: ExportFormat;
  label: string;
  hint: string;
  icon: string;
}

const FORMATS: FormatOption[] = [
  { key: 'sql', label: 'PostgreSQL', hint: 'Format natif (.sql)', icon: 'ri-database-2-line' },
  { key: 'mysql', label: 'MySQL', hint: 'Compatible MySQL (.sql)', icon: 'ri-server-line' },
  { key: 'json', label: 'JSON', hint: 'Données + schéma (.json)', icon: 'ri-braces-line' },
  { key: 'csv', label: 'CSV', hint: 'Un fichier par table (.zip)', icon: 'ri-file-excel-2-line' },
];

const MIME: Record<ExportFormat, string> = {
  sql: 'application/sql',
  mysql: 'application/sql',
  json: 'application/json',
  csv: 'text/csv',
};

interface Props {
  identity: CallerIdentity;
  tables: DbTable[];
}

export default function ExportPanel({ identity, tables }: Props) {
  const [format, setFormat] = useState<ExportFormat>('sql');
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(tables.map((t) => t.name)));

  const allNames = tables.map((t) => t.name);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? tables.filter((t) => t.name.toLowerCase().includes(q)) : tables;
  }, [tables, query]);

  const selectedCount = selected.size;
  const allSelected = selectedCount === allNames.length && allNames.length > 0;

  const toggleOne = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allNames));
  };

  const toggleVisible = () => {
    setSelected((prev) => {
      const visibleNames = filtered.map((t) => t.name);
      const allVisibleSelected = visibleNames.length > 0 && visibleNames.every((n) => prev.has(n));
      const next = new Set(prev);
      visibleNames.forEach((n) => (allVisibleSelected ? next.delete(n) : next.add(n)));
      return next;
    });
  };

  const handleExport = async () => {
    if (selectedCount === 0) {
      setStatus({ type: 'error', text: 'Sélectionnez au moins une table à exporter.' });
      return;
    }
    setExporting(true);
    setStatus(null);
    try {
      const tableList = allNames.filter((n) => selected.has(n));
      const res = await runDatabaseExport(identity, format, tableList);
      if (format === 'csv') {
        const files = res.files || [];
        if (files.length === 0) throw new Error('Aucune donnée à exporter.');
        await downloadCsvZip(files);
        setStatus({ type: 'success', text: `${files.length} fichier(s) CSV empaqueté(s) dans un ZIP.` });
      } else {
        if (!res.content) throw new Error('Export vide.');
        downloadText(res.filename || 'zifek-database.sql', res.content, MIME[format]);
        setStatus({ type: 'success', text: `Export « ${res.filename} » téléchargé (${tableList.length} table(s)).` });
      }
    } catch (e) {
      setStatus({ type: 'error', text: e instanceof Error ? e.message : 'Échec de l\u2019export.' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-download-2-line text-lg"></i>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground-950">Exporter la base</h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            Choisissez les tables à exporter (toute la base par défaut) puis le format de téléchargement.
          </p>
        </div>
      </div>

      {/* --- Sélection des tables --- */}
      <div className="mb-4 border border-background-200/70 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-background-100/70 hover:bg-background-100 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-checkbox-multiple-line text-foreground-500"></i>
            <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">
              {selectedCount === 0
                ? 'Aucune table sélectionnée'
                : allSelected
                  ? `Toutes les tables (${allNames.length})`
                  : `${selectedCount} table(s) sélectionnée(s)`}
            </span>
          </div>
          <i className={`text-foreground-400 transition-transform ${pickerOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
        </button>

        {pickerOpen && (
          <div className="border-t border-background-200/70 p-3">
            <div className="relative mb-2">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une table…"
                className="w-full h-9 pl-9 pr-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>

            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={toggleAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <i className={allSelected ? 'ri-checkbox-indeterminate-line' : 'ri-checkbox-multiple-line'}></i>
                {allSelected ? 'Tout décocher' : 'Tout cocher'}
              </button>
              {query.trim() && (
                <button
                  type="button"
                  onClick={toggleVisible}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  <i className="ri-filter-3-line"></i>
                  Les {filtered.length} résultats
                </button>
              )}
            </div>

            <div className="max-h-56 overflow-y-auto border border-background-200/70 rounded-md divide-y divide-background-200/60">
              {filtered.map((t) => {
                const checked = selected.has(t.name);
                return (
                  <label
                    key={t.name}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-background-100/60 cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(t.name)}
                      className="w-4 h-4 accent-[oklch(var(--primary-500))] cursor-pointer"
                    />
                    <span className="text-sm text-foreground-900 truncate flex-1">{t.name}</span>
                    <span className="text-[11px] text-foreground-400 whitespace-nowrap">
                      {t.row_count.toLocaleString('fr-FR')} lignes
                    </span>
                  </label>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-xs text-foreground-400 py-6 text-center">Aucune table correspondante.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- Formats --- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {FORMATS.map((f) => {
          const active = format === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFormat(f.key)}
              className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors cursor-pointer ${
                active
                  ? 'border-primary-300 bg-primary-50/70'
                  : 'border-background-200/70 bg-background-100 hover:bg-background-200/60'
              }`}
            >
              <i className={`${f.icon} text-lg ${active ? 'text-primary-600' : 'text-foreground-500'}`}></i>
              <span className="text-xs font-semibold text-foreground-900 whitespace-nowrap">{f.label}</span>
              <span className="text-[10px] text-foreground-400 leading-tight">{f.hint}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          onClick={handleExport}
          disabled={exporting || selectedCount === 0}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Export en cours…
            </>
          ) : (
            <>
              <i className="ri-download-2-line"></i>
              Exporter {allSelected ? 'la base' : `${selectedCount} table(s)`} ({FORMATS.find((f) => f.key === format)?.label})
            </>
          )}
        </button>
        <p className="text-[11px] text-foreground-400 leading-relaxed">
          Export en lecture seule · aucune donnée n&apos;est modifiée ni supprimée.
        </p>
      </div>

      {status && (
        <div
          className={`mt-3 p-3 rounded-lg text-xs flex items-center gap-2 ${
            status.type === 'success'
              ? 'bg-accent-50 border border-accent-200 text-accent-900'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <i className={status.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
          {status.text}
        </div>
      )}
    </div>
  );
}